import React from 'react';
import {
  Button,
  Checkbox,
  ControlLink,
  Input,
  LoadingBalls,
  Overlay,
  PasswordInput,
  Stack,
  Text
} from '@moda/om';
import { noop, useForm } from '@moda/portal-stanchions';
import { AuthAction, AuthPayloadSuccess } from '../../lib/authenticate';
import { useAuth } from '../../hooks/useAuth';
import { AccountLocked } from './AccountLocked';
import { SocialAuthButtons } from './SocialAuthButtons';

export type SignInFormValues = {
  signInEmail: string;
  signInPassword: string;
  signInRemember: boolean;
};

export type PasswordSignInProps = {
  title?: string;
  message?: string;
  className?: string;
  showSocialAuth?: boolean;
  showCancelButton?: boolean;
  defaultValues?: Partial<SignInFormValues>;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
  onClickForgotPassword?: () => void;
  onAuth: (authUser?: AuthPayloadSuccess) => void;
};

const SIGN_IN_SUBMIT = 'SIGN IN';
export const FORGOT_PASSWORD_TEXT = 'Forgot Password?';

const SIGN_IN_VALIDATIONS = {
  signInEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  },
  signInPassword: {
    required: {
      message: 'Invalid password, please try again.',
      value: true
    }
  }
};

export const PasswordSignIn: React.FC<
  Required<Pick<PasswordSignInProps, 'title' | 'message'>> & PasswordSignInProps
> = ({
  onAuth,
  className,
  showSocialAuth = true,
  showCancelButton,
  defaultValues,
  showAuthModal,
  hideAuthModal,
  onClickForgotPassword,
  title,
  message
}) => {
  const { error, handleAuth, loading } = useAuth(AuthAction.SignIn);

  const onSubmit = (values: SignInFormValues) => {
    handleAuth({
      email: values.signInEmail,
      password: values.signInPassword,
      remember: values.signInRemember
    })
      .then(onAuth)
      .catch(noop);
  };

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit,
    validations: SIGN_IN_VALIDATIONS
  });

  const errorMessage = error?.code === 'account_locked' ? <AccountLocked /> : error?.message;

  return (
    <>
      <div className={className} data-testid="signInSection">
        <form onSubmit={handleSubmit}>
          <div className="Authentication__title">{title}</div>
          <div className="Authentication__message">{message}</div>
          {errorMessage && (
            <div className="Authentication__error" role="alert">
              {errorMessage}
            </div>
          )}

          <Stack space={5}>
            <div className="Authentication__fields">
              <Input
                className="Authentication__input"
                defaultValue={defaultValues?.signInEmail}
                error={fieldErrors.signInEmail?.message || error != null}
                id="signInEmail"
                data-testid="signInEmail"
                placeholder="Email"
                type="email"
              />

              <PasswordInput
                className="Authentication__input"
                error={fieldErrors.signInPassword?.message || error != null}
                id="signInPassword"
                data-testid="signInPassword"
                placeholder="Password"
              />

              <div className="Authentication__bottomFields">
                <div className="Authentication__rememberMe">
                  <Checkbox
                    className="Authentication__checkbox"
                    defaultChecked={defaultValues?.signInRemember ?? true}
                    id="signInRemember"
                    data-testid="signInRemember"
                    type="checkbox"
                  >
                    Remember me
                  </Checkbox>
                </div>

                <div className="Authentication__forgotPassword" data-testid="signInForgotPassword">
                  {onClickForgotPassword ? (
                    <ControlLink onClick={onClickForgotPassword} type="button">
                      {FORGOT_PASSWORD_TEXT}
                    </ControlLink>
                  ) : (
                    <ControlLink to="/forgot-password">{FORGOT_PASSWORD_TEXT}</ControlLink>
                  )}
                </div>
              </div>
            </div>

            <Stack space={2}>
              <Button
                className="Authentication__button Authentication__button--sign-in"
                type="submit"
                data-testid="signInSubmit"
              >
                {SIGN_IN_SUBMIT}
              </Button>

              {showCancelButton && hideAuthModal && (
                <Button
                  className="Authentication__button Authentication__button--cancel-sign-in"
                  onClick={hideAuthModal}
                  secondary
                  type="button"
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        </form>

        {showSocialAuth && (
          <>
            <div className="Authentication__divider" />

            <SocialAuthButtons
              action={AuthAction.SignIn}
              showAmazonMessage
              showAuthModal={showAuthModal}
              hideAuthModal={hideAuthModal}
              onAuth={onAuth}
            />

            <div className="Authentication__privacy-policy">
              <ControlLink to="/privacy" rel="noopener noreferrer" target="_blank" underlined>
                Privacy Policy
              </ControlLink>
            </div>
          </>
        )}
      </div>

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
